import { AchPaymentFilter } from '@treasury/domain/ach';
import { defaultAchPaymentFilterDto } from '@treasury/domain/ach/data/ach.data';
import {
    type CurrencyRange,
    CurrencyRangeType,
} from '@treasury/presentation/components/tm-currency-range';
import { mapCurrencyRangeToDto } from '@treasury/presentation/components/tm-currency-range.mappings';
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import { mapDateRangeToDto } from '@treasury/presentation/components/tm-date-range.mappings';
import { add, sub } from 'date-fns';

const defaultAmountRange: CurrencyRange = {
    rangeType: CurrencyRangeType.AllAmounts,
};

const defaultInitiatedRange: DateRange = {
    rangeType: DateRangeType.None,
};

const defaultNextPaymentRange: DateRange = {
    rangeType: DateRangeType.None,
};

const defaultEffectiveRange: DateRange = {
    rangeType: DateRangeType.DateRange,
    startDate: sub(new Date(), { weeks: 1 }),
    endDate: add(new Date(), { weeks: 1 }),
};

const defaultRecurringEffectiveRange: DateRange = {
    rangeType: DateRangeType.None,
};

export class AchPaymentFilterVm extends AchPaymentFilter {
    constructor(dto = defaultAchPaymentFilterDto) {
        super(dto);
        this.reset();
    }

    private _debitRange = defaultAmountRange;

    private _creditRange = defaultAmountRange;

    private _initiatedRange = defaultInitiatedRange;

    private _effectiveRange = defaultEffectiveRange;

    private _nextPaymentRange = defaultNextPaymentRange;

    public get debitRange(): CurrencyRange {
        return this._debitRange;
    }

    public set debitRange(r: CurrencyRange) {
        this._debitRange = r;
        const { operator, fromAmount, toAmount, specificAmount } = mapCurrencyRangeToDto(r, false);
        this.debitAmountType = operator || '';
        this.debitAmountMin = fromAmount;
        this.debitAmountMax = toAmount;
        this.debitAmount = specificAmount;
    }

    public get creditRange(): CurrencyRange {
        return this._creditRange;
    }

    public set creditRange(r: CurrencyRange) {
        this._creditRange = r;
        const { operator, fromAmount, toAmount, specificAmount } = mapCurrencyRangeToDto(r, false);
        this.creditAmountType = operator || '';
        this.creditAmountMin = fromAmount;
        this.creditAmountMax = toAmount;
        this.creditAmount = specificAmount;
    }

    public get initiatedRange(): DateRange {
        return this._initiatedRange;
    }

    public set initiatedRange(r: DateRange) {
        this._initiatedRange = r;
        const { operator, dateFrom, dateTo, specificDate } = mapDateRangeToDto(r, false);
        this.initiatedDateType = operator || '';
        this.initiatedDateStart = dateFrom;
        this.initiatedDateEnd = dateTo;
        this.initiatedDate = specificDate;
    }

    public get effectiveRange(): DateRange {
        return this._effectiveRange;
    }

    public set effectiveRange(r: DateRange) {
        this._effectiveRange = r;
        const { operator, dateFrom, dateTo, specificDate } = mapDateRangeToDto(r, false);
        this.effectiveDateType = operator || '';
        this.effectiveDateStart = dateFrom;
        this.effectiveDateEnd = dateTo;
        this.effectiveDate = specificDate;
    }

    public get nextPaymentRange(): DateRange {
        return this._nextPaymentRange;
    }

    public set nextPaymentRange(r: DateRange) {
        this._nextPaymentRange = r;
        const { operator, dateFrom, dateTo, specificDate } = mapDateRangeToDto(r, false);
        this.nextPaymentDateType = operator || '';
        this.nextPaymentDateStart = dateFrom;
        this.nextPaymentDateEnd = dateTo;
        this.nextPaymentDate = specificDate;
    }

    public reset() {
        super.reset();
        this.debitRange = { ...defaultAmountRange };
        this.creditRange = { ...defaultAmountRange };
        this.initiatedRange = { ...defaultInitiatedRange };
        this.effectiveRange = this.isRecurring
            ? { ...defaultRecurringEffectiveRange }
            : { ...defaultEffectiveRange };
        this.nextPaymentRange = { ...defaultNextPaymentRange };
    }
}
